import React, {useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {getBorder, showModalReprocessKyc, showSnackbar, useHistory} from 'utils'
import {useWindowLayout, useWindowMode} from 'windows'
import {Menu, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {useDispatch, useSelector} from 'lib/redux'
import {GiftShopExploreSensitivitySlider} from '../SensitivitySlider'
import {GiftShopExploreSubHeaderProps} from './GiftShopExploreSubHeaderProps'

interface StyledAccuracyButtonProps {
  active?: boolean
}

const StyledButtonBase = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${convertUnit(32)};
  padding: ${convertUnit(8)} ${convertUnit(12)};
  border: ${({theme}) => getBorder(1, 'solid', theme.primary_5)};
  border-radius: ${convertUnit(8)};
  box-sizing: border-box;
  cursor: pointer;
`

const StyledButtonAccuracy = styled(
  StyledButtonBase,
)<StyledAccuracyButtonProps>`
  background-color: ${({theme, active}) =>
    active ? theme.primary_5 : 'transparent'};
`

const StyledSliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertUnit(4)};
`

export default function GiftShopExploreSubHeader({
  stateRangeValue,
  stateActionToggle,
}: GiftShopExploreSubHeaderProps) {
  const {translate} = useTranslation()
  const {update} = useDispatch()
  const history = useHistory()
  const mode = useWindowMode()
  const {width} = useWindowLayout()
  const {kycStatus: roboyuStatus} = useSelector('yuserActivationState')
  const {status: similarStatus} = useSelector('similarAccountStatus')
  const [actionToggle, setActionToggle] = stateActionToggle

  const handlePatchSensitivityLevel = useCallback(
    (accuracy: number) => {
      requestData('giftshop_patch_sensitivity_level', {
        data: {sensitivity_level: accuracy},
        onRequestSuccess: ({status}) => {
          if (status === 200) {
            update('roboyuSensitivityLevel', {sensitivityLevel: accuracy})
            update('giftshopState', {exploreRefresher: new Date()})
            showSnackbar(translate('giftShop:roboyuAccuracySuccessSnackbar'))
          }
        },
      })
    },
    [translate, update],
  )

  const handleRenderSlider = useMemo(
    () => (
      <StyledSliderContainer>
        <GiftShopExploreSensitivitySlider
          isFloating
          stateRangeValue={stateRangeValue}
          thumbColor="primary_5"
          thumbType="arrow"
          descPosition="bottom"
          onSlide={handlePatchSensitivityLevel}
        />
      </StyledSliderContainer>
    ),
    [handlePatchSensitivityLevel, stateRangeValue],
  )

  const handleRenderSensitivityButton = useMemo(
    () => (
      <Menu
        stateToggle={stateActionToggle}
        renderContent={handleRenderSlider}
        containerStyle={{width: '100%'}}
        contentContainerStyle={{
          right: mode !== 'mobile' ? 0 : undefined,
          minWidth: mode !== 'mobile' ? convertUnit(410) : undefined,
          width: mode === 'mobile' ? width - 40 : undefined,
          borderRadius: convertUnit(16),
          padding: `${convertUnit(28)} ${convertUnit(16)} ${convertUnit(8)}`,
        }}>
        <StyledButtonAccuracy
          active={actionToggle}
          onClick={() => setActionToggle(true)}>
          <Paragraph color={actionToggle ? 'white_1' : 'primary_5'}>
            {translate('global:accuracy')}
          </Paragraph>
        </StyledButtonAccuracy>
      </Menu>
    ),
    [
      actionToggle,
      handleRenderSlider,
      mode,
      setActionToggle,
      stateActionToggle,
      translate,
      width,
    ],
  )

  const handleRenderRoboyuButton = useMemo(
    () => (
      <StyledButtonBase
        onClick={() => {
          update('giftShopTooltipState', {isClickedAccuracy: true})
          if (roboyuStatus === 'onhold' && similarStatus === 'CHOOSE_SELF') {
            showModalReprocessKyc()
            return
          }
          history.push('giftshop_robopet', {})
        }}>
        <Paragraph color="primary_5">{translate('giftShop:robopet')}</Paragraph>
      </StyledButtonBase>
    ),
    [history, roboyuStatus, similarStatus, translate, update],
  )

  return (
    <>
      {handleRenderSensitivityButton}
      {handleRenderRoboyuButton}
    </>
  )
}
